import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Download, ExternalLink, FileQuestion, FileText, FileX, Minus, Plus } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import type { CvKind } from '@nest-recruit/shared';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { Button } from '../Button';
import { Skeleton } from '../Skeleton';
import { documentUrl, loadPdf } from './pdf-cache';
import styles from './CvViewer.module.css';

/** Zoom steps on top of "fit to width" — the default a recruiter reads at. */
const ZOOMS = [0.75, 1, 1.25, 1.5, 2] as const;
const FIT = 1;
/** Pages further than a screen away give their canvas back — how a 60-page CV stays light (SCR-01 §13). */
const NEAR_MARGIN = '100% 0px';

/** The CV to show — sorting mode's card and the interview cockpit both carry this shape. */
export interface CvFile {
  readonly documentId: string;
  readonly fileName: string;
  readonly kind: CvKind | null;
  readonly isRemoved: boolean;
}

interface CvViewerProps {
  readonly cv: CvFile | null;
  readonly candidateName: string;
  /** What to do without the CV, in the words of the screen it sits on. */
  readonly fallback?: string;
  /** R5 (RNK-02): words whose lines are marked on the page — the ranking's evidence. */
  readonly highlights?: readonly string[];
  /** Opt-in (RNK-02, Redesign Group 3): the file's size, so the bar reads "2 pages · 214 KB". */
  readonly sizeBytes?: number | null;
  /** Opt-in: the title with no CV, in the words of the screen it sits on ("The CV is on its way"). */
  readonly emptyTitle?: string;
}

const SORTING_FALLBACK = 'Decide from the answers on the right.';
const NO_HIGHLIGHTS: readonly string[] = [];

/**
 * SCR-01's left region. A PDF renders here with PDF.js (S15 Q4) — in the page, not
 * in the browser's own viewer, so A/R/P/H keep working after a click on the CV. A Word
 * file or a missing file shows what it is and a way to get it (SCR-01 state c), and
 * sorting carries on either way.
 */
export function CvViewer({ cv, candidateName, fallback = SORTING_FALLBACK, highlights = NO_HIGHLIGHTS, sizeBytes = null, emptyTitle = 'No CV on this application' }: CvViewerProps): ReactNode {
  if (!cv) {
    return <Placeholder icon={FileX} title={emptyTitle} body={fallback} />;
  }
  if (cv.isRemoved) {
    return <Placeholder icon={FileX} title="This CV was removed under the retention policy" body={`The record of it remains. ${fallback}`} />;
  }
  if (cv.kind !== 'pdf') {
    return (
      <Placeholder
        icon={cv.kind === 'word' ? FileText : FileQuestion}
        title={cv.kind === 'word' ? 'Word files cannot be previewed' : 'This file cannot be previewed'}
        body={`Download ${candidateName}'s CV to read it.`}
        action={<DownloadLink documentId={cv.documentId} fileName={cv.fileName} isProminent />}
      />
    );
  }
  return <PdfDocument key={cv.documentId} documentId={cv.documentId} fileName={cv.fileName} fallback={fallback} highlights={highlights} sizeBytes={sizeBytes} />;
}

const KILOBYTE = 1024;
const MEGABYTE = KILOBYTE * KILOBYTE;

/** "214 KB", "2.1 MB" — as the mockup's CV bar. */
export function formatFileSize(bytes: number): string {
  if (bytes >= MEGABYTE) return `${(bytes / MEGABYTE).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / KILOBYTE))} KB`;
}

/** The CV bar's second line: "2 pages · 214 KB", "Word · 64 KB"; the pages only once the PDF has loaded. */
export function cvMetaLine(lead: string | null, sizeBytes: number | null): string | null {
  const parts = [lead, sizeBytes === null ? null : formatFileSize(sizeBytes)].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(' · ') : null;
}

function pagesLabel(count: number): string | null {
  if (count <= 0) return null;
  return count === 1 ? '1 page' : `${count} pages`;
}

/**
 * The file's name and its size line, with Download — the PDF toolbar's left side, and on its own the bar over a
 * Word CV's text (RNK-02, Redesign Group 3), so both read the same.
 */
export function CvFileBar({ documentId, fileName, meta, children }: { readonly documentId: string; readonly fileName: string; readonly meta: string | null; readonly children?: ReactNode }): ReactNode {
  return (
    <div className={styles.toolbar} role="toolbar" aria-label="CV">
      <FileLabel fileName={fileName} meta={meta} />
      <div className={styles.tools}>
        {children}
        {isPdfName(fileName) && <NewTabLink documentId={documentId} />}
        <DownloadLink documentId={documentId} fileName={fileName} />
      </div>
    </div>
  );
}

function FileLabel({ fileName, meta }: { readonly fileName: string; readonly meta: string | null }): ReactNode {
  return (
    <span className={styles.fileLabel}>
      <FileText size={16} aria-hidden className={styles.fileIcon} />
      <span className={styles.fileText}>
        <span className={styles.fileName} title={fileName}>
          {fileName}
        </span>
        {meta !== null && <span className={styles.fileMeta}>{meta}</span>}
      </span>
    </span>
  );
}

interface PdfInfo {
  readonly document: PDFDocumentProxy;
  readonly width: number;
  readonly height: number;
}

function PdfDocument({
  documentId,
  fileName,
  fallback,
  highlights,
  sizeBytes,
}: {
  readonly documentId: string;
  readonly fileName: string;
  readonly fallback: string;
  readonly highlights: readonly string[];
  readonly sizeBytes: number | null;
}): ReactNode {
  const pdf = useQuery<PdfInfo, Error>({
    queryKey: ['cv', documentId],
    queryFn: async () => {
      const document = await loadPdf(documentId);
      const first = (await document.getPage(1)).getViewport({ scale: 1 });
      return { document, width: first.width, height: first.height };
    },
    staleTime: Infinity,
    // The document is shared with the prefetch cache, which owns its lifetime.
    structuralSharing: false,
    retry: 1,
  });
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  const [zoom, setZoom] = useState<number>(FIT);
  const [page, setPage] = useState(1);
  const placeholders = useRef(new Map<number, HTMLDivElement>());

  // Focus lands on the CV so ↑/↓ scroll it without a click (SCR-01 §10).
  useEffect(() => {
    scrollRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry?.contentRect.width ?? 0));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const info = pdf.data;
  // The scroll area's padding is outside its content box, so this is the room a page has.
  const scale = info && width > 0 ? (width / info.width) * zoom : 0;
  const pageHeight = info ? info.height * scale : 0;
  const pageCount = info?.document.numPages ?? 0;

  function goTo(number: number): void {
    const target = placeholders.current.get(Math.min(Math.max(number, 1), pageCount));
    if (target) scrollRef.current?.scrollTo({ top: target.offsetTop, behavior: 'smooth' });
  }

  /** The page crossing the top third of the view is the one being read. */
  function onScroll(): void {
    const element = scrollRef.current;
    if (!element) return;
    const line = element.scrollTop + element.clientHeight / 3;
    let current = 1;
    for (const [number, placeholder] of placeholders.current) {
      if (placeholder.offsetTop <= line && number > current) current = number;
    }
    setPage(current);
  }

  const zoomIndex = ZOOMS.findIndex((step) => step === zoom);

  return (
    <div className={styles.viewer}>
      <CvFileBar documentId={documentId} fileName={fileName} meta={cvMetaLine(pagesLabel(pageCount), sizeBytes)}>
          <Button size="sm" variant="ghost" icon={ChevronUp} aria-label="Previous page" disabled={page <= 1} onClick={() => goTo(page - 1)} />
          <span className={styles.pageCount} aria-live="polite">
            {pageCount > 0 ? `Page ${page} of ${pageCount}` : '—'}
          </span>
          <Button size="sm" variant="ghost" icon={ChevronDown} aria-label="Next page" disabled={page >= pageCount} onClick={() => goTo(page + 1)} />
          <Button size="sm" variant="ghost" icon={Minus} aria-label="Zoom out" disabled={zoomIndex <= 0} onClick={() => setZoom(ZOOMS[zoomIndex - 1] ?? zoom)} />
          <Button size="sm" variant="ghost" onClick={() => setZoom(FIT)} disabled={zoom === FIT}>
            Fit
          </Button>
          <Button size="sm" variant="ghost" icon={Plus} aria-label="Zoom in" disabled={zoomIndex >= ZOOMS.length - 1} onClick={() => setZoom(ZOOMS[zoomIndex + 1] ?? zoom)} />
      </CvFileBar>
      <div ref={scrollRef} className={styles.scroll} tabIndex={0} aria-label="CV pages. Arrow keys scroll." onScroll={onScroll}>
        {pdf.isError && (
          <Placeholder
            icon={FileX}
            title="This CV could not be opened"
            body={`Open it in a new tab, or download it. ${fallback}`}
            action={
              <span className={styles.tools}>
                <NewTabLink documentId={documentId} isProminent />
                <DownloadLink documentId={documentId} fileName={fileName} isProminent />
              </span>
            }
          />
        )}
        {!info && !pdf.isError && (
          <div className={styles.loadingPage}>
            <Skeleton shape="card" />
          </div>
        )}
        {info && scale > 0 && (
          <Pages document={info.document} count={pageCount} scale={scale} width={info.width * scale} height={pageHeight} root={scrollRef} placeholders={placeholders} highlights={highlights} />
        )}
      </div>
    </div>
  );
}

interface PagesProps {
  readonly document: PDFDocumentProxy;
  readonly count: number;
  readonly scale: number;
  readonly width: number;
  readonly height: number;
  readonly root: RefObject<HTMLDivElement | null>;
  readonly placeholders: RefObject<Map<number, HTMLDivElement>>;
  readonly highlights: readonly string[];
}

/** Every page keeps its place; only the ones near the view hold a drawn canvas. */
function Pages({ document, count, scale, width, height, root, placeholders, highlights }: PagesProps): ReactNode {
  const [near, setNear] = useState<ReadonlySet<number>>(() => new Set([1, 2]));

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        setNear((current) => {
          const next = new Set(current);
          for (const entry of entries) {
            const number = Number((entry.target as HTMLElement).dataset.page);
            if (entry.isIntersecting) next.add(number);
            else next.delete(number);
          }
          return next;
        });
      },
      { root: root.current, rootMargin: NEAR_MARGIN },
    );
    for (const element of placeholders.current.values()) observer.observe(element);
    return () => observer.disconnect();
  }, [count, root, placeholders]);

  return (
    <div className={styles.pages}>
      {Array.from({ length: count }, (_, index) => index + 1).map((number) => (
        <div
          key={number}
          data-page={number}
          ref={(element) => {
            if (element) placeholders.current.set(number, element);
            else placeholders.current.delete(number);
          }}
          className={styles.page}
          style={{ width, height }}
        >
          {near.has(number) && <PageCanvas document={document} number={number} scale={scale} width={width} height={height} />}
          {near.has(number) && highlights.length > 0 && <PageHighlights document={document} number={number} scale={scale} words={highlights} />}
        </div>
      ))}
    </div>
  );
}

function PageCanvas({ document, number, scale, width, height }: { readonly document: PDFDocumentProxy; readonly number: number; readonly scale: number; readonly width: number; readonly height: number }): ReactNode {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    let isCurrent = true;
    let cancel: (() => void) | null = null;
    void document.getPage(number).then((page) => {
      const canvas = canvasRef.current;
      if (!isCurrent || !canvas) return;
      // Drawn at the screen's real pixel density, shown at layout size: sharp text.
      const ratio = window.devicePixelRatio || 1;
      const viewport = page.getViewport({ scale: scale * ratio });
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      const task = page.render({ canvas, viewport });
      cancel = () => task.cancel();
      task.promise.catch(() => undefined);
    });
    return () => {
      isCurrent = false;
      cancel?.();
    };
  }, [document, number, scale]);

  return <canvas ref={canvasRef} className={styles.canvas} style={{ width, height }} aria-label={`Page ${number}`} />;
}

interface HighlightBox {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/** Case and accents folded, as the ranking matched them (§L30). */
function fold(text: string): string {
  return text.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * RNK-02: the lines of the page's text layer holding any of the words, marked over the
 * drawn page. pdf.js gives each text run its place, so no text is drawn twice.
 */
function PageHighlights({ document, number, scale, words }: { readonly document: PDFDocumentProxy; readonly number: number; readonly scale: number; readonly words: readonly string[] }): ReactNode {
  const [boxes, setBoxes] = useState<readonly HighlightBox[]>([]);
  const key = words.join(' ');

  useEffect(() => {
    let isCurrent = true;
    const wanted = key.split(' ').map(fold).filter((word) => word.trim() !== '');
    void (async () => {
      const page = await document.getPage(number);
      const viewport = page.getViewport({ scale });
      const { items } = await page.getTextContent();
      const found: HighlightBox[] = [];
      for (const item of items) {
        if (!('str' in item) || !wanted.some((word) => fold(item.str).includes(word))) continue;
        const [x, y] = viewport.convertToViewportPoint(item.transform[4] ?? 0, item.transform[5] ?? 0);
        const height = Math.hypot(item.transform[2] ?? 0, item.transform[3] ?? 0) * scale;
        found.push({ left: x, top: y - height, width: item.width * scale, height });
      }
      if (isCurrent) setBoxes(found);
    })().catch(() => undefined);
    return () => {
      isCurrent = false;
    };
  }, [document, number, scale, key]);

  return boxes.map((box, index) => <span key={index} className={styles.highlight} style={box} aria-hidden />);
}

/**
 * D6 (workflow review 2026-10-29): every CV opens in a new tab too, through the same permission-checked file link the viewer reads.
 * Only a PDF: a browser can show it, while a Word file would just download.
 */
function NewTabLink({ documentId, isProminent = false }: { readonly documentId: string; readonly isProminent?: boolean }): ReactNode {
  return (
    <a className={isProminent ? styles.downloadProminent : styles.download} href={documentUrl(documentId)} target="_blank" rel="noopener noreferrer">
      <ExternalLink size={14} aria-hidden />
      Open in new tab
    </a>
  );
}

const PDF_NAME = /\.pdf$/i;

function isPdfName(fileName: string): boolean {
  return PDF_NAME.test(fileName);
}

function DownloadLink({ documentId, fileName, isProminent = false }: { readonly documentId: string; readonly fileName: string; readonly isProminent?: boolean }): ReactNode {
  return (
    <a className={isProminent ? styles.downloadProminent : styles.download} href={documentUrl(documentId)} download={fileName}>
      <Download size={14} aria-hidden />
      Download
    </a>
  );
}

function Placeholder({ icon: Icon, title, body, action }: { readonly icon: typeof FileX; readonly title: string; readonly body: string; readonly action?: ReactNode }): ReactNode {
  return (
    <div className={styles.placeholder}>
      <Icon size={28} aria-hidden className={styles.placeholderIcon} />
      <p className={styles.placeholderTitle}>{title}</p>
      <p className={styles.placeholderBody}>{body}</p>
      {action}
    </div>
  );
}
